import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, LogOut, RefreshCw, Search, MessageCircle, Lock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const http = axios.create({ baseURL: API, withCredentials: true });
const STATUSES = ["Baru", "Diproses", "Selesai", "Batal"];
const BADGE = { Baru: "bg-amber-100 text-amber-800", Diproses: "bg-sky-100 text-sky-800", Selesai: "bg-emerald-100 text-emerald-800", Batal: "bg-zinc-200 text-zinc-700" };

const errText = (d) => (typeof d === "string" ? d : Array.isArray(d) ? d.map((e) => e?.msg).filter(Boolean).join(" ") : "Terjadi kesalahan. Coba lagi.");
const waNum = (p) => { const d = String(p).replace(/\D/g, ""); return d.startsWith("0") ? `62${d.slice(1)}` : d; };
const fmtDate = (s) => { try { return new Date(s).toLocaleString("id-ID", { dateStyle: "medium", timeStyle: "short" }); } catch { return s; } };

const Login = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const { data } = await http.post("/auth/login", { email, password });
      onLogin(data);
    } catch (err) {
      setError(errText(err.response?.data?.detail));
    } finally { setBusy(false); }
  };
  return (
    <div className="mx-auto max-w-md px-5 pt-32 pb-24 sm:pt-40">
      <form data-testid="admin-login-form" onSubmit={submit} className="rounded-[2rem] bg-white p-8 ring-1 ring-leaf/10">
        <span className="grid h-12 w-12 place-items-center rounded-full bg-[var(--mint)] text-[var(--leaf)]"><Lock className="h-5 w-5" /></span>
        <h1 className="mt-5 font-display text-3xl font-black text-[var(--leaf-deep)]">Admin Minaqu</h1>
        <p className="mt-2 text-sm text-[var(--muted-ink)]">Masuk untuk melihat pesanan dari formulir website.</p>
        <input data-testid="admin-login-email" type="email" required placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-6 w-full rounded-2xl border border-leaf/20 px-4 py-3 text-sm outline-none focus:border-[var(--leaf)]" />
        <input data-testid="admin-login-password" type="password" required placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-3 w-full rounded-2xl border border-leaf/20 px-4 py-3 text-sm outline-none focus:border-[var(--leaf)]" />
        {error && <p data-testid="admin-login-error" className="mt-3 text-sm text-red-700">{error}</p>}
        <button data-testid="admin-login-submit" disabled={busy} className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[var(--leaf)] px-6 py-3.5 font-display text-sm font-bold text-white disabled:opacity-60">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} Masuk
        </button>
      </form>
    </div>
  );
};

const OrderCard = ({ o, onStatus }) => (
  <article data-testid={`admin-order-${o.id}`} className="rounded-[1.5rem] bg-white p-5 ring-1 ring-leaf/10">
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        <p className="font-display text-lg font-black text-[var(--ink)]">{o.name}</p>
        <p className="text-xs text-[var(--muted-ink)]">{fmtDate(o.created_at)} · No. {o.id.slice(0, 8)} · {o.order_type}</p>
      </div>
      <select data-testid={`admin-order-status-${o.id}`} value={o.status} onChange={(e) => onStatus(o.id, e.target.value)}
        className={`rounded-full px-3 py-1.5 text-xs font-bold outline-none ${BADGE[o.status] || BADGE.Baru}`}>
        {STATUSES.map((s) => <option key={s}>{s}</option>)}
      </select>
    </div>
    <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-sm">
      <a href={`https://wa.me/${waNum(o.phone)}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-semibold text-[var(--leaf)]"><MessageCircle className="h-3.5 w-3.5" />{o.phone}</a>
      {o.email && <a href={`mailto:${o.email}`} className="text-[var(--muted-ink)]">{o.email}</a>}
      {o.city && <span className="text-[var(--muted-ink)]">{o.city}</span>}
    </div>
    {o.items?.length > 0 && (
      <ul className="mt-4 divide-y divide-leaf/10 rounded-2xl bg-[var(--paper)] px-4 text-sm">
        {o.items.map((i, n) => <li key={n} className="flex justify-between gap-3 py-2"><span>{i.name}</span><span className="shrink-0 text-[var(--muted-ink)]">x{i.qty}{i.price ? ` · ${i.price}` : ""}</span></li>)}
      </ul>
    )}
    {o.notes && <p className="mt-3 whitespace-pre-line text-sm text-[var(--muted-ink)]"><b className="text-[var(--ink)]">Catatan:</b> {o.notes}</p>}
  </article>
);

const Dashboard = ({ user, onLogout }) => {
  const [orders, setOrders] = useState(null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("Semua");
  const load = async () => {
    try {
      const { data } = await http.get("/admin/orders");
      setOrders(Array.isArray(data) ? data : []);
    } catch (err) {
      if (err.response?.status === 401) onLogout(); else toast.error("Gagal memuat pesanan");
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const onStatus = async (id, status) => {
    try {
      const { data } = await http.patch(`/admin/orders/${id}`, { status });
      setOrders((prev) => prev.map((o) => (o.id === id ? data : o)));
      toast.success(`Status diubah ke ${status}`);
    } catch { toast.error("Gagal mengubah status"); }
  };
  const list = useMemo(() => (orders || []).filter((o) => (filter === "Semua" || o.status === filter) &&
    `${o.name} ${o.phone} ${o.city} ${o.notes} ${o.items?.map((i) => i.name).join(" ")}`.toLowerCase().includes(q.toLowerCase())), [orders, q, filter]);
  const count = (s) => (orders || []).filter((o) => o.status === s).length;

  return (
    <div className="mx-auto max-w-[1100px] px-5 pt-28 pb-24 sm:px-8 sm:pt-32">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="eyebrow">Admin · {user.email}</p>
          <h1 className="mt-3 font-display text-4xl sm:text-5xl font-black tracking-tight text-[var(--leaf-deep)]">Pesanan masuk</h1>
        </div>
        <div className="flex gap-2">
          <button data-testid="admin-refresh-btn" onClick={load} className="inline-flex items-center gap-2 rounded-full border border-leaf/25 bg-white px-4 py-2 text-sm font-semibold"><RefreshCw className="h-4 w-4" /> Muat ulang</button>
          <button data-testid="admin-logout-btn" onClick={onLogout} className="inline-flex items-center gap-2 rounded-full bg-[var(--ink)] px-4 py-2 text-sm font-semibold text-white"><LogOut className="h-4 w-4" /> Keluar</button>
        </div>
      </div>
      <div className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-4">
        {STATUSES.map((s) => (
          <div key={s} data-testid={`admin-stat-${s.toLowerCase()}`} className="rounded-2xl bg-white p-4 ring-1 ring-leaf/10">
            <p className="font-display text-3xl font-black text-[var(--leaf)]">{orders ? count(s) : "–"}</p>
            <p className="text-xs text-[var(--muted-ink)]">{s}</p>
          </div>
        ))}
      </div>
      <div className="mt-6 flex flex-col gap-3 sm:flex-row">
        <label className="relative flex-1">
          <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--muted-ink)]" />
          <input data-testid="admin-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari nama, nomor, tanaman…" className="w-full rounded-full border border-leaf/20 bg-white py-2.5 pl-10 pr-4 text-sm outline-none focus:border-[var(--leaf)]" />
        </label>
        <select data-testid="admin-status-filter" value={filter} onChange={(e) => setFilter(e.target.value)} className="rounded-full border border-leaf/20 bg-white px-4 py-2.5 text-sm">
          {["Semua", ...STATUSES].map((s) => <option key={s}>{s}</option>)}
        </select>
      </div>
      <div className="mt-6 grid gap-4">
        {orders === null && <Loader2 className="mx-auto h-6 w-6 animate-spin text-[var(--leaf)]" />}
        {orders && list.length === 0 && <p data-testid="admin-orders-empty" className="py-16 text-center text-[var(--muted-ink)]">Belum ada pesanan.</p>}
        {list.map((o) => <OrderCard key={o.id} o={o} onStatus={onStatus} />)}
      </div>
    </div>
  );
};

export default function Admin() {
  const [user, setUser] = useState(null);
  useEffect(() => {
    http.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(false));
  }, []);
  const logout = async () => {
    try { await http.post("/auth/logout"); } catch { /* ignore */ }
    setUser(false);
  };
  if (user === null) return <div className="grid min-h-[70vh] place-items-center"><Loader2 className="h-6 w-6 animate-spin text-[var(--leaf)]" /></div>;
  return user ? <Dashboard user={user} onLogout={logout} /> : <Login onLogin={setUser} />;
}
